import React from "react";
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
} from "@mui/material";

export type CheckboxSize = "small" | "medium" | "large";
export type CheckboxIntent =
  | "default"
  | "primary"
  | "inherit"
  | "inherit-white";

/**
 * 디자인 시스템 체크박스 props — MUI `Checkbox` 의 size/color 만 자체 토큰으로 교체.
 *
 * @example
 * ```tsx
 * <Checkbox checked={isChecked} onChange={(e) => setChecked(e.target.checked)} />
 * <Checkbox size="small" intent="primary" />
 * ```
 */
export interface CheckboxProps
  extends Omit<MuiCheckboxProps, "size" | "color"> {
  /** 크기 토큰 (기본 `"medium"`) */
  size?: CheckboxSize;
  /** 색상 의도 토큰 — 어두운 배경 위에서는 `"inherit-white"` 사용 */
  intent?: CheckboxIntent;
}

const CheckboxStyles = {
  sizes: {
    small: {
      width: 32,
      height: 32,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "--dsq-hover-diam": "34px",
    },
    medium: {
      width: 40,
      height: 40,
      "& .MuiSvgIcon-root": { fontSize: 20 },
      "--dsq-hover-diam": "40px",
    },
    large: {
      width: 48,
      height: 48,
      "& .MuiSvgIcon-root": { fontSize: 24 },
      "--dsq-hover-diam": "48px",
    },
  },
  intents: {
    primary: {
      color: "#1D74FF",
      "&.Mui-checked": {
        color: "#1D74FF",
      },
      "--dsq-hover-bg": "rgba(29, 116, 255, 0.10)",
      "--dsq-active-bg": "rgba(29, 116, 255, 0.12)",
      "--dsq-focus-ring": "rgba(29, 116, 255, 0.24)",
    },
    "inherit-white": {
      color: "#FFFFFF",
      "&.Mui-checked": {
        color: "#FFFFFF",
      },
      "--dsq-hover-bg": "rgba(255, 255, 255, 0.08)",
      "--dsq-active-bg": "rgba(255, 255, 255, 0.12)",
      "--dsq-focus-ring": "rgba(255, 255, 255, 0.24)",
    },
    inherit: {
      color: "rgba(0, 0, 0, 0.87)",
      "&.Mui-checked": {
        color: "rgba(0, 0, 0, 0.87)",
      },
      "--dsq-hover-bg": "rgba(0, 0, 0, 0.04)",
      "--dsq-active-bg": "rgba(0, 0, 0, 0.08)",
      "--dsq-focus-ring": "rgba(0, 0, 0, 0.12)",
    },
    default: {
      color: "rgba(0, 0, 0, 0.56)",
      "&.Mui-checked": {
        color: "rgba(0, 0, 0, 0.56)",
      },
      "--dsq-hover-bg": "rgba(0, 0, 0, 0.06)",
      "--dsq-active-bg": "rgba(0, 0, 0, 0.10)",
      "--dsq-focus-ring": "rgba(0, 0, 0, 0.20)",
    },
  },
  disabled: {
    primary: {
      "&.Mui-disabled": { color: "#1D74FF" },
      "&.Mui-disabled:hover::before": { opacity: 0 },
    },
    "inherit-white": {
      "&.Mui-disabled": { color: "#FFFFFF" },
      "&.Mui-disabled:hover::before": { opacity: 0 },
    },
    inherit: {
      "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.87)" },
      "&.Mui-disabled:hover::before": { opacity: 0 },
    },
    default: {
      "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.56)" },
      "&.Mui-disabled:hover::before": { opacity: 0 },
    },
  },
} as const;

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ size = "medium", intent = "default", disabled, sx, ...muiProps }, ref) => {
    const sizeSx = CheckboxStyles.sizes[size];
    const intentSx = CheckboxStyles.intents[intent];
    const disabledSx = disabled ? CheckboxStyles.disabled[intent] : {};
    const mergedSx = sx
      ? { ...sizeSx, ...intentSx, ...disabledSx, ...sx }
      : { ...sizeSx, ...intentSx, ...disabledSx };

    return (
      <MuiCheckbox ref={ref} disabled={disabled} sx={mergedSx} {...muiProps} />
    );
  }
);

Checkbox.displayName = "Checkbox";
